import { useState } from "react";

export default function ProblemesCoffre() {
  const [open, setOpen] = useState<number | null>(null);

  const symptomes = [
    "Bruit de claquement ou vibration anormale provenant du coffre",
    "Fuite d'eau ou trace d'humidité sous le coffre à l'intérieur",
    "Coffre déformé, bombé ou fissuré visiblement depuis l'extérieur",
    "Tablier qui heurte le bord du coffre en montant ou en descendant",
    "Odeur de brûlé ou surchauffe ressentie au niveau du coffre",
    "Mouvement du tablier irrégulier avec à-coups ou points durs",
  ];

  const causes = [
    "Déformation du coffre due à un choc (échelle, grêle, vent violent)",
    "Corrosion ou oxydation des parties métalliques internes du caisson",
    "Rupture des fixations murales ou des équerres de maintien du coffre",
    "Usure des joints d'étanchéité supérieurs laissant passer l'eau de pluie",
    "Désaxage du tablier provoquant un frottement répété contre le coffre",
    "Surcharge du moteur ou du mécanisme à cause d'un coffre mal calibré",
  ];

  return (
    <section id="problemes" className="bg-white py-14 md:py-20">
      <div className="max-w-4xl mx-auto px-4 md:px-8">
        <h2 className="text-2xl md:text-3xl font-bold text-brand-blue mb-6">
          Problèmes de coffre de volet roulant
        </h2>
        <p className="text-brand-text leading-relaxed mb-4">
          Le coffre, aussi appelé caisson ou linteau, est l'enveloppe protectrice qui abrite le tablier enroulé, le moteur ou le treuil, et les éléments de fixation. C'est une pièce structurale essentielle : si elle est déformée, corrodée ou mal fixée, tout le fonctionnement du volet est compromis. Un coffre endommagé peut provoquer des bruits, des fuites, un frottement du tablier, ou même un risque de chute du caisson dans les cas extrêmes.
        </p>
        <p className="text-brand-text leading-relaxed mb-8">
          Chez Dépan'Volets Strasbourg, nous intervenons sur tous les types de coffres : bloc-baie (intégré à la fenêtre), coffre extérieur sur linteau, coffre intégré dans la maçonnerie, et coffres de rénovation. Notre équipe dispose d'outils adaptés pour démonter, redresser, réparer ou remplacer un coffre sans endommager la façade ni le tablier. Nous travaillons sur des coffres en aluminium, PVC, acier galvanisé et bois, pour des volets de toutes marques.
        </p>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
          <div className="bg-brand-light rounded-xl p-5 border border-brand-border">
            <h3 className="text-lg font-semibold text-brand-blue mb-4 flex items-center gap-2">
              <span className="w-8 h-8 flex items-center justify-center rounded-full bg-brand-cyan/10 text-brand-cyan">
                <i className="ri-error-warning-line"></i>
              </span>
              Signes d'un coffre endommagé
            </h3>
            <ul className="space-y-2">
              {symptomes.map((s, i) => (
                <li key={i} className="flex items-start gap-2 text-sm text-brand-text">
                  <i className="ri-checkbox-circle-line text-brand-cyan mt-0.5 shrink-0"></i>
                  {s}
                </li>
              ))}
            </ul>
          </div>
          <div className="bg-brand-light rounded-xl p-5 border border-brand-border">
            <h3 className="text-lg font-semibold text-brand-blue mb-4 flex items-center gap-2">
              <span className="w-8 h-8 flex items-center justify-center rounded-full bg-brand-cyan/10 text-brand-cyan">
                <i className="ri-tools-line"></i>
              </span>
              Causes principales de détérioration
            </h3>
            <ul className="space-y-2">
              {causes.map((c, i) => (
                <li key={i} className="flex items-start gap-2 text-sm text-brand-text">
                  <i className="ri-checkbox-circle-line text-brand-cyan mt-0.5 shrink-0"></i>
                  {c}
                </li>
              ))}
            </ul>
          </div>
        </div>

        <div className="bg-white rounded-xl border border-brand-border p-5">
          <h3 className="text-lg font-semibold text-brand-blue mb-4">
            Diagnostic visuel du coffre
          </h3>
          <div className="space-y-3">
            {[
              { q: "Inspectez le contour du coffre depuis l'extérieur", a: "Recherchez des fissures, des déformations, des traces de rouille ou des joints d'étanchéité décollés. Un coffre bombé vers le bas indique souvent une rupture des fixations internes." },
              { q: "Vérifiez l'intérieur en ouvrant le volet", a: "Si le coffre est accessible, regardez l'état des équerres de fixation, des supports moteur, et des guides d'enroulement. La corrosion commence souvent dans ces zones peu aérées." },
              { q: "Testez le mouvement du tablier en montée et descente", a: "Un tablier qui frotte contre le bord du coffre ou qui fait un bruit de claquement révèle un désaxage ou une déformation progressive du caisson." },
              { q: "Contrôlez l'étanchéité après une pluie", a: "Des traces d'humidité ou de condensation sous le coffre, ou sur le mur en dessous, signalent un joint supérieur défectueux ou une fissure dans la coque." },
            ].map((item, i) => (
              <div key={i} className="border border-brand-border rounded-lg overflow-hidden">
                <button
                  onClick={() => setOpen(open === i ? null : i)}
                  className="w-full flex items-center justify-between p-4 text-left hover:bg-brand-light/50 transition-colors"
                >
                  <span className="font-medium text-brand-blue text-sm md:text-base">{item.q}</span>
                  <i className={`ri-arrow-down-s-line text-brand-cyan transition-transform ${open === i ? "rotate-180" : ""}`}></i>
                </button>
                {open === i && (
                  <div className="px-4 pb-4 text-sm text-brand-text leading-relaxed">
                    {item.a}
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}